import {Box, Flex, FormLabel, Text} from '@chakra-ui/react';
import { currencyFormat } from 'components/Number';
import ChartTooltip from 'components/charts/ChartTooltip';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import SelectInput from "../../../../../components/inputs/SelectInput";
import {generateSelectOptions} from "../../../../../helpers/inputHelper";

const CHART_TYPE = {
  BAR: 'bar',
  AREA: 'area',
};

const CHART_TYPE_META = {
  [CHART_TYPE.BAR]: {
    label: 'Bar',
  },
  [CHART_TYPE.AREA]: {
    label: 'Area',
  },
};

const SpendBarChart = ({ data }) => {
  const [chartData, setChartData] = useState();
  const [chartType, setChartType] = useState(CHART_TYPE.BAR);

  useEffect(() => {
    if (data) {
      const _chartData = getChartData(chartType);
      setChartData(_chartData);
    }
  }, [data, chartType]);

  const getCategoryLabel = (mDate, index) => {
    if (index === 0 || mDate.month() === 0) {
      return [mDate.format('MMM'), mDate.format('YYYY')];
    }

    return mDate.format('MMM');
  };

  const getChartOptions = ({ dates, xAxisCategories, maxTotal }) => {
    return {
      chart: {
        fontFamily: 'DM Sans',
        stacked: true,
        animations:{
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const total = series[0][dataPointIndex] + series[1][dataPointIndex];
          const series1 = w.config.series[0];
          const series2 = w.config.series[1];
          const title = dataPointIndex >=w.config.dates.length?"---":w.config.dates[dataPointIndex].format('MMM YYYY');

          let tooltipItems = [];

          tooltipItems.push(
              { label: 'Total', value: currencyFormat(total) },
              {
                label: 'Actual', // series1.name,
                value: currencyFormat(series1.data[dataPointIndex]),
                color: series1.color,
              },
              {
                label: 'Predicted', // series2.name,
                value: currencyFormat(series2.data[dataPointIndex]),
                color: series2.color,
              },
          )

          const r = renderToString(<ChartTooltip title={title} items={tooltipItems} />)
          return r;
        },
      } ,
      xaxis: {
        categories: xAxisCategories,
      },
      yaxis: {
        min: 0,
        max: () => {
          return maxTotal;
        },
        labels: {
          formatter: (value) => {
            if (value === null) {
              return null;
            }
            return currencyFormat(value);
          },
        },
      },
      dates,
    };
  };

  const getChartData = (chartType) => {
    const _data = data.map((row) => {
      return {
        date: moment({
          year: row.date.year,
          month: row.date.month - 1,
          day: row.date.day,
        }),
        actualMarketingSpend: row.actual_marketing_spend || 0,
        predictedMarketingSpend: row.predicted_marketing_spend || 0,
        totalMarketingSpend: (row.actual_marketing_spend || 0) + (row.predicted_marketing_spend || 0),
        marketing_per_source: row.marketing_per_source,
      };
    });

    _data.sort((a, b) => a.date - b.date);

    let actualMarketingSpendData = [];
    let predictedMarketingSpendData = [];
    let totalMarketingSpendData = [];
    let xAxisCategories = [];
    let dates = [];
    let maxTotalValue = 0;

    let spendBySource = {}
    let spendBySourceKeys = []

    _data.forEach((row, index) => {
      if (row.marketing_per_source) {
          row.marketing_per_source.forEach((mps) => {
              if (!spendBySourceKeys.includes(mps.Source)) {
                spendBySourceKeys.push(mps.Source)
                spendBySource[mps.Source] = Array(_data.length).fill(0);
              }
          });
      }
    });

    _data.forEach((row, index) => {
      actualMarketingSpendData.push(row.actualMarketingSpend);
      predictedMarketingSpendData.push(row.predictedMarketingSpend);
      totalMarketingSpendData.push(row.totalMarketingSpend);
      xAxisCategories.push(getCategoryLabel(row.date, index));
      dates.push(row.date);
      if (row.totalMarketingSpend > maxTotalValue) {
        maxTotalValue = row.totalMarketingSpend;
      }
      if (row.marketing_per_source) {
        row.marketing_per_source.forEach((mps) => {
          spendBySource[mps.Source][index] = mps.spend || 0;
        });
      }
    });

    const series = [
      {
        name: 'actualMarketingSpendData',
        data: actualMarketingSpendData,
        type: chartType,
        color: '#84D9FD',
        stroke: {
          show: true,
          curve: 'smooth',
        }
      },
      {
        name: 'predictedMarketingSpendData',
        data: predictedMarketingSpendData,
        type: chartType,
        color: '#775FFC',
        stroke: {
          show: true,
          curve: 'smooth',
        }
      },
    ];

    const options1 = getChartOptions({
      dates,
      xAxisCategories,
      maxTotal: maxTotalValue,
    });

    const r = {
      series: series,
      options: options1,
      chartType: chartType,
    };
    return r;
  };

  if (!chartData) {
    return null;
  }

  return (
    <>
      <Flex justifyContent={'space-between'} alignItems={'center'}>
        <Text fontWeight={800} mb={10}>
          Marketing Monthly Spend By Type
        </Text>
        <Flex align={'center'} gap={1}>
          <FormLabel m={0}>Chart type: </FormLabel>
          <SelectInput
              options={generateSelectOptions({ metaObj: CHART_TYPE_META })}
              value={chartType}
              onChange={setChartType}
              w={200}
          />
        </Flex>
      </Flex>
      <Box minH={300}>
        <Chart
          type={chartData.chartType}
          series={chartData.series}
          options={chartData.options}
          width={'100%'}
          height={'100%'}
        />
      </Box>
    </>
  );
};

SpendBarChart.propTypes = {
  data: PropTypes.array,
};

export default SpendBarChart;
